<template>
  <div class="space-y-3">
    <!-- Header with unread badge -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <h4 class="text-sm font-medium text-slate-200">Recipient Replies</h4>
        <span
          v-if="unreadCount > 0"
          class="inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold text-white bg-red-500 rounded-full min-w-[18px]"
        >
          {{ unreadCount }}
        </span>
      </div>
      <button
        v-if="entries.length > 0"
        @click="loadEntries"
        class="text-[11px] text-primary-400 hover:text-primary-300 transition-colors"
      >
        Refresh
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center gap-2 py-4">
      <div class="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
      <span class="text-xs text-slate-400">Loading replies&hellip;</span>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="entries.length === 0"
      class="py-6 text-center"
    >
      <svg class="w-8 h-8 mx-auto text-slate-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
      <p class="text-xs text-slate-500">No replies yet</p>
    </div>

    <!-- Reply entries -->
    <div v-else class="space-y-2 max-h-[500px] overflow-y-auto pr-1">
      <div
        v-for="entry in entries"
        :key="entry.id"
        class="rounded-lg border transition-colors cursor-pointer"
        :class="[
          entry.is_read
            ? 'bg-slate-800/20 border-slate-700/30 hover:bg-slate-800/40'
            : 'bg-slate-800/50 border-primary-500/30 hover:bg-slate-800/60',
        ]"
        @click="toggleExpand(entry)"
      >
        <!-- Reply header -->
        <div class="flex items-start gap-3 p-3">
          <!-- Unread indicator -->
          <div class="flex-shrink-0 mt-1.5">
            <div
              :class="[
                'w-2 h-2 rounded-full',
                entry.is_read ? 'bg-slate-600' : 'bg-primary-400',
              ]"
            ></div>
          </div>

          <!-- Content summary -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-0.5">
              <span class="text-xs font-medium text-white truncate">
                {{ entry.from_name || entry.from_email }}
              </span>
              <span class="text-[10px] text-slate-500 flex-shrink-0">
                {{ formatTime(entry.created_at) }}
              </span>
            </div>
            <p
              v-if="entry.subject"
              class="text-[11px] text-slate-300 truncate mb-0.5"
            >
              {{ entry.subject }}
            </p>
            <p class="text-[11px] text-slate-400 truncate">
              {{ truncateBody(entry.body_text) }}
            </p>
          </div>

          <!-- Expand chevron -->
          <svg
            class="w-4 h-4 text-slate-500 flex-shrink-0 mt-1 transition-transform"
            :class="{ 'rotate-180': expandedId === entry.id }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <!-- Expanded body + reply form -->
        <div
          v-if="expandedId === entry.id"
          class="border-t border-slate-700/40 px-3 pb-3"
        >
          <!-- Full email body -->
          <div class="py-3 text-xs text-slate-300 leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-y-auto">
            {{ entry.body_text || '(no text content)' }}
          </div>

          <!-- Reply composer -->
          <div class="mt-2 space-y-2">
            <textarea
              v-model="replyText"
              rows="3"
              class="w-full text-xs bg-slate-900/60 border border-slate-600/50 rounded-md px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500/50 resize-none"
              placeholder="Type your reply..."
            ></textarea>
            <div class="flex items-center justify-between">
              <span class="text-[10px] text-slate-500">
                Reply to {{ entry.from_email }}
              </span>
              <button
                @click.stop="sendReplyToEntry(entry)"
                :disabled="!replyText.trim() || sending"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-primary-600 hover:bg-primary-500 disabled:bg-slate-600 disabled:text-slate-400 rounded-md transition-colors"
              >
                <svg v-if="sending" class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                {{ sending ? 'Sending...' : 'Send Reply' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import {
  getInboxEntries,
  getUnreadCount,
  markInboxRead,
  sendReply,
  type InboxEntry,
} from '@/lib/alertService'
/** Dispatch a toast notification via the app's custom event system. */
function showToast(type: 'success' | 'error', message: string) {
  window.dispatchEvent(new CustomEvent('toast', { detail: { type, message } }))
}

const props = defineProps<{
  checkoutId: number
  /** Name of the current user (checkout owner) for reply emails. */
  senderName?: string
}>()

const emit = defineEmits<{
  (e: 'unreadChange', count: number): void
}>()
const entries = ref<InboxEntry[]>([])
const loading = ref(false)
const unreadCount = ref(0)
const expandedId = ref<number | null>(null)
const replyText = ref('')
const sending = ref(false)

/** Truncate body text for preview line. */
function truncateBody(text: string | null): string {
  if (!text) return '(no content)'
  const cleaned = text.replace(/\r?\n/g, ' ').trim()
  return cleaned.length > 120 ? cleaned.slice(0, 120) + '...' : cleaned
}

/** Format timestamp relative to now. */
function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffDay = Math.floor(diffHr / 24)
    if (diffDay < 7) return `${diffDay}d ago`
    return d.toLocaleDateString()
  } catch {
    return iso
  }
}

/** Toggle expand and mark as read. */
async function toggleExpand(entry: InboxEntry) {
  if (expandedId.value === entry.id) {
    expandedId.value = null
    replyText.value = ''
    return
  }

  expandedId.value = entry.id
  replyText.value = ''

  // Mark as read if unread
  if (!entry.is_read) {
    try {
      await markInboxRead(entry.id)
      entry.is_read = true
      unreadCount.value = Math.max(0, unreadCount.value - 1)
      emit('unreadChange', unreadCount.value)
    } catch {
      // Non-blocking
    }
  }
}

/** Send reply to the expanded inbox entry. */
async function sendReplyToEntry(entry: InboxEntry) {
  if (!replyText.value.trim()) return
  sending.value = true

  try {
    await sendReply({
      checkout_id: props.checkoutId,
      contact_id: entry.contact_id,
      inbox_id: entry.id,
      to_email: entry.from_email,
      to_name: entry.from_name,
      subject: entry.subject,
      body_text: replyText.value.trim(),
      sender_name: props.senderName ?? null,
    })

    showToast('success', 'Reply sent successfully')
    replyText.value = ''
    expandedId.value = null
  } catch (err) {
    showToast('error', 'Failed to send reply')
    console.error('[InboxPanel] Send reply failed:', err)
  } finally {
    sending.value = false
  }
}

/** Load entries and unread count. */
async function loadEntries() {
  loading.value = true
  try {
    const [inboxData, count] = await Promise.all([
      getInboxEntries(props.checkoutId),
      getUnreadCount(props.checkoutId),
    ])
    entries.value = inboxData
    unreadCount.value = count
    emit('unreadChange', count)
  } catch {
    // Error logged in service
  } finally {
    loading.value = false
  }
}

onMounted(loadEntries)
watch(() => props.checkoutId, loadEntries)
</script>
